import Box from '@mui/material/Box';
import { storyblokEditable } from '@storyblok/react';
import debounce from 'lodash/debounce';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import config from '../../../config';
import SearchPageContent from '../../../containers/SearchPage/SearchPage.content';
import { setActiveListing, setSearchPageTab } from '../../../containers/SearchPage/SearchPage.duck';
import {
  validFilterParams,
  validSortParams,
  validURLParamsForExtendedData,
} from '../../../containers/SearchPage/SearchPage.helpers';
import { SEARCH_WITH_MAP_DEBOUNCE } from '../../../containers/SearchPage/SearchPage.util';
import { getListingsById } from '../../../ducks/marketplaceData.duck';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import routeConfiguration from '../../../routeConfiguration';
import useAppSelector from '../../../util/hooks/useAppSelector';
import { getLocaleFromPath } from '../../../util/locale';
import { isCordova } from '../../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../../util/routes';
import { parse } from '../../../util/urlHelpers';
import { mapboxBoundsToSDKBounds } from '../../SearchMap/SearchMapWithMapbox.util';
import { withBaseComponent } from '../hoc/withBaseComponent';
import StoryblokHerosearchPreset from '../presets/StoryblokHerosearchPreset';

/**
 * @param {object} props
 * @param {object} props.blok
 */
const StoryblokSearchResultComponent = ({ blok }) => {
  const { query, amountListings, shouldShowSearchMap = true } = blok;

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { searchInProgress, searchListingsError, currentPageResultIds, pagination } =
    useAppSelector(
      (state) => ({
        searchInProgress: state.SearchPage.searchInProgress,
        searchListingsError: state.SearchPage.searchListingsError,
        currentPageResultIds: state.SearchPage.currentPageResultIds,
        pagination: state.SearchPage.pagination,
      }),
      shallowEqual,
    );

  const dispatch = useDispatch();

  const entities = useAppSelector((state) => state.marketplaceData.entities);
  const listings = useMemo(
    () => getListingsById(entities, currentPageResultIds),
    [entities, currentPageResultIds],
  );

  const [isSearchMapOpenOnMobile, setIsSearchMapOpenOnMobile] = useState(shouldShowSearchMap);

  const parsedQuery = parse(query, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const filterConfig = config.custom.filters;
  const sortConfig = config.custom.sortConfig;
  const onMapMoveEnd = debounce((viewportBoundsChanged, data) => {
    const { viewportBounds, viewportCenter } = data;

    //only update every kilometer if the currentLocation is followed

    const routes = routeConfiguration();

    // intl.locale doesn't always reflect the current route's locale (it lags
    // behind while IntlProvider async-loads messages after a locale switch),
    // so read it from window.location which is authoritative. Using intl.locale
    // here sent the user to a wrong/non-existent locale path → 404.
    const newLocale = getLocaleFromPath(
      window.location.hash.split('#')[1] || window.location.pathname,
    );

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    const hasSameBoundsCorners =
      viewportBounds &&
      viewportBounds.ne.lat === viewportBounds.sw.lat &&
      viewportBounds.ne.lng === viewportBounds.sw.lng;
    if (isCordova() && hasSameBoundsCorners) {
      return;
    }
    if (
      (viewportBoundsChanged && !window.followCurrentLocation) ||
      (viewportBoundsChanged && window.followCurrentLocation && window.updateSearchPage) ||
      (viewportBoundsChanged && window.followCurrentLocation && window.watchZoomChanged)
    ) {
      if (window.updateSearchPage) {
        window.updateSearchPage = false;
        window.watchFirstLocation = null;
      }
      if (window.watchZoomChanged) {
        window.watchZoomChanged = false;
      }

      // using window.location.search here to get the current query parameters
      const search =
        window.location.search ||
        new URLSearchParams(window.location.hash.split('?')[1] || '').toString();
      // parse query parameters, including a custom attribute named category
       
      const { mobileSearch, address, bounds, mapSearch, ...rest } = parse(search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      //const viewportMapCenter = SearchMap.getMapCenter(map);
      const originMaybe = config.sortSearchByDistance ? { origin: viewportCenter } : {};

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfig),
        ...validSortParams(rest, sortConfig),
      };

      if (mobileSearch === 'open') return;
      navigate(
        createResourceLocatorString('SearchPage', routes, {}, searchParams, newLocale),
      );
    }
  }, SEARCH_WITH_MAP_DEBOUNCE);

  const { mapSearch, page, ...searchInURL } = parsedQuery;

  const validQueryParams = validURLParamsForExtendedData(searchInURL, filterConfig);
  const searchParamsForPagination = parse(location.search);

  const isWindowDefined = typeof window !== 'undefined';

  const onMapIconClick = useCallback(() => {
    setIsSearchMapOpenOnMobile(true);
    dispatch(setSearchPageTab('map'));
  }, [dispatch]);

  const onListIconClick = useCallback(() => {
    setIsSearchMapOpenOnMobile(false);
    dispatch(setSearchPageTab('listings'));
  }, [dispatch]);

  const onCloseModalInMobile = () => {
    setIsSearchMapOpenOnMobile(false);
    dispatch(setSearchPageTab('listings'));
  };

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch],
  );
  const onActivateListing = useCallback(
    (listingId) => dispatch(setActiveListing(listingId)),
    [dispatch],
  );

  let { bounds, origin } = searchInURL || {};

  if (isWindowDefined && window.autoZoomBounds) {
    bounds = mapboxBoundsToSDKBounds(window.autoZoomBounds);
  }

  const urlCategory = params.slug;

  // Pagination has to stay on the Storyblok page itself — the filters live in
  // the blok's `query`, and loadData merges the `page` url param back into it.
  const paginationPathParams = useMemo(() => ({ slug: params.slug, rest: params['*'] }), [params]);

  return (
    <>
      <Box mb={4}>
        <StoryblokHerosearchPreset blok={blok} />
      </Box>
      <Box {...storyblokEditable(blok)} sx={{ display: 'flex', width: '100%' }}>
        <SearchPageContent
          amountListings={amountListings ? parseInt(amountListings) : 100}
          bounds={bounds}
          isSearchMapOpenOnMobile={isSearchMapOpenOnMobile}
          isStoryblok={true}
          listings={listings}
          onActivateListing={onActivateListing}
          onCloseModalInMobile={onCloseModalInMobile}
          onListIconClick={onListIconClick}
          onManageDisableScrolling={onManageDisableScrolling}
          onMapIconClick={onMapIconClick}
          onMapMoveEnd={onMapMoveEnd}
          origin={origin}
          pagination={pagination}
          paginationPageName="StoryblokPage"
          paginationPathParams={paginationPathParams}
          searchInProgress={searchInProgress}
          searchListingsError={searchListingsError}
          searchParamsAreInSync={true}
          searchParamsForPagination={searchParamsForPagination}
          setIsSearchMapOpenOnMobile={setIsSearchMapOpenOnMobile}
          shouldShowSearchMap={isSearchMapOpenOnMobile}
          tab="listings"
          urlCategory={urlCategory}
          validQueryParams={validQueryParams}
        />
      </Box>
    </>
  );
};

StoryblokSearchResultComponent.displayName = 'StoryblokSearchResult';
const StoryblokSearchResult = withBaseComponent(StoryblokSearchResultComponent);

export default memo(StoryblokSearchResult);
